import React from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Layers, Database, Workflow, TrendingUp, Server, ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";

const SECTIONS = [
  { icon: <Layers size={22} />, label: "Vision", title: "Built for Institutional Operations",
    desc: "Tychi was developed to address the fundamental operational challenges faced by fund administrators, managers, and financial institutions. Traditional systems fragment data across disconnected tools, creating inefficiencies, compliance risks, and operational bottlenecks.",
    points: ["Eliminate data silos across accounting, operations, and reporting", "Reduce operational risk through automated workflows and controls", "Achieve real-time visibility across all fund operations"] },
  { icon: <Server size={22} />, label: "Architecture", title: "Modular Platform Design",
    desc: "Tychi is built on a modular architecture that allows each component to operate independently while maintaining deep integration across the entire platform. This design enables targeted upgrades and consistent data integrity.",
    points: ["Independent modules with shared data infrastructure", "API-driven architecture enabling seamless integration", "Component-level scalability without system-wide impact"] },
  { icon: <Database size={22} />, label: "Data Infrastructure", title: "Structured Financial Data",
    desc: "At the core of Tychi is a structured financial data layer designed specifically for institutional operations. Every transaction, record, and document is organized within a consistent data model ensuring integrity and compliance.",
    points: ["Normalized data model optimized for financial operations", "Full transaction lineage with complete audit trails", "Real-time data consistency across all platform modules"] },
  { icon: <Workflow size={22} />, label: "Automation", title: "AI-Assisted Operational Automation",
    desc: "Tychi automates critical operational workflows including request management, approval chains, document processing, and notification routing — with AI-assisted intelligence to eliminate manual handoffs and accelerate operational throughput.",
    points: ["Configurable approval workflows with multi-level authorization", "AI-assisted document routing and notification management", "Exception-based monitoring for operational oversight"] },
  { icon: <TrendingUp size={22} />, label: "Scalability", title: "Built to Grow",
    desc: "Tychi's modular architecture is designed for expansion. New modules, integrations, and capabilities can be added without disrupting existing operations, from single-fund to complex multi-fund environments.",
    points: ["Horizontal scaling across fund structures and entities", "New module integration without system downtime", "Performance-optimized for growing data volumes"] },
];

export default function PlatformPage() {
  useScrollReveal();
  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="platform-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">The Platform</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Unified Financial <strong>Infrastructure</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 560 }}>
            A modular, AI-enabled platform designed to consolidate accounting, workflows, and operational 
            management into a single system — delivered as part of our managed fund administration services.
          </p>
        </section>
      </div>

      {SECTIONS.map((s, i) => (
        <section key={i} className={i % 2 === 0 ? "w-section" : "w-section-full w-section-surface"}>
          <div className={i % 2 === 0 ? "" : "w-section"} style={i % 2 === 0 ? {} : { paddingTop: "6rem", paddingBottom: "6rem" }}>
            <div className={`w-split ${i % 2 === 1 ? "reverse" : ""}`}>
              <div className={i % 2 === 0 ? "w-reveal-left" : "w-reveal-right"}>
                <div className="w-label">{s.label}</div>
                <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)" }}>{s.title}</h2>
                <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>{s.desc}</p>
                <ul className="w-features">
                  {s.points.map((p, j) => <li key={j}><span className="check-icon"><Check size={12} /></span> {p}</li>)}
                </ul>
              </div>
              <div className={`${i % 2 === 0 ? "w-reveal-right" : "w-reveal-left"} w-delay-2`} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: "100%", maxWidth: 360, aspectRatio: "1", borderRadius: 20, border: "1px solid var(--w-border)", background: "var(--w-surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "var(--w-blue)", marginBottom: "1rem" }}>{React.cloneElement(s.icon, { size: 56 })}</div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: 500 }}>{s.label}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="w-cta-section">
        <div className="w-reveal">
          <h2 className="w-title">Explore the <strong>Modules</strong></h2>
          <p className="w-subtitle" style={{ margin: "0 auto 2rem", maxWidth: 450 }}>See how Tychi Portal and Tychi GL work together.</p>
          <Link to="/modules" className="w-btn w-btn-primary">View Modules <ArrowRight size={16} /></Link>
        </div>
      </section>
    </WebsiteLayout>
  );
}
